/**
 * Workflow RPC schemas — wire shapes for the Hub-managed workflow surface.
 *
 * All operations here back Hub admin UI (list / read / create / update /
 * status / item read). Tool-driven write paths (create item, transition,
 * comment) are agent-only and ride the capability tool surface; Hub
 * itself does not write to items in the MVP.
 *
 * The workflow definition schemas are intentionally loose at the wire
 * boundary — itemSchema is arbitrary JSON Schema, predicate is arbitrary
 * JsonLogic. The gateway's save-time validators (`workflow-validators.ts`)
 * enforce the operator subset and structural invariants; the wire layer
 * is a tolerant carrier.
 */

import { z } from "zod";

const WorkflowActorRefSchema = z.union([
  z.object({ type: z.literal("agent"), id: z.string() }),
  z.object({ type: z.literal("user"), id: z.string() }),
]);

const WorkflowTriggerSchema = z.union([
  z.object({
    type: z.literal("agent_session"),
    name: z.string(),
    agentId: z.string(),
    prompt: z.string().optional(),
  }),
  z.object({
    type: z.literal("assign"),
    name: z.string(),
    to: WorkflowActorRefSchema.nullable(),
  }),
  z.object({
    type: z.literal("field_update"),
    name: z.string(),
    set: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("http"),
    name: z.string(),
    url: z.string(),
    method: z.enum(["POST", "PUT"]).optional(),
    headers: z.record(z.string(), z.string()).optional(),
    body: z.record(z.string(), z.unknown()).optional(),
  }),
]);

const WorkflowStateSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
});

const WorkflowTransitionSchema = z.object({
  name: z.string(),
  fromState: z.string().nullable(),
  toState: z.string(),
  validation: z.object({
    predicate: z.unknown().optional(),
    predicateDescription: z.string().optional(),
    actors: z.array(WorkflowActorRefSchema),
  }),
  triggers: z.array(WorkflowTriggerSchema),
});

const WorkflowDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  itemSchema: z.record(z.string(), z.unknown()),
  states: z.array(WorkflowStateSchema),
  transitions: z.array(WorkflowTransitionSchema),
  participants: z.object({
    agents: z.array(z.string()),
    users: z.array(z.string()),
  }),
  status: z.enum(["active", "paused", "archived"]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkflowDefinitionWire = z.infer<typeof WorkflowDefinitionSchema>;

const WorkflowValidationErrorSchema = z.object({
  path: z.array(z.string()),
  code: z.string(),
  message: z.string(),
  value: z.unknown().optional(),
});

// ---------------------------------------------------------------------------
// workflow.list
// ---------------------------------------------------------------------------

export const WorkflowListPayloadSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  includeArchived: z.boolean().optional(),
});
export type WorkflowListPayload = z.infer<typeof WorkflowListPayloadSchema>;

export const WorkflowListDataSchema = z.object({
  workflows: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
      status: z.enum(["active", "paused", "archived"]),
      itemCount: z.number().optional(),
      updatedAt: z.string().optional(),
    }),
  ),
  nextCursor: z.string().optional(),
});
export type WorkflowListData = z.infer<typeof WorkflowListDataSchema>;

// ---------------------------------------------------------------------------
// workflow.get — full admin view (mirrors `workflow_get_definition` tool)
// ---------------------------------------------------------------------------

export const WorkflowGetPayloadSchema = z.object({
  workflowId: z.string(),
});
export type WorkflowGetPayload = z.infer<typeof WorkflowGetPayloadSchema>;

export const WorkflowGetDataSchema = z.object({
  definition: WorkflowDefinitionSchema,
  itemCount: z.number(),
  openItemCount: z.number(),
  filePath: z.string(),
  validationErrors: z.array(WorkflowValidationErrorSchema),
});
export type WorkflowGetData = z.infer<typeof WorkflowGetDataSchema>;

// ---------------------------------------------------------------------------
// workflow.create
// ---------------------------------------------------------------------------

export const WorkflowCreatePayloadSchema = z.object({
  definition: WorkflowDefinitionSchema,
});
export type WorkflowCreatePayload = z.infer<typeof WorkflowCreatePayloadSchema>;

export const WorkflowCreateDataSchema = z.union([
  z.object({
    ok: z.literal(true),
    definition: WorkflowDefinitionSchema,
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("validation_failed"),
    errors: z.array(WorkflowValidationErrorSchema),
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("conflict"),
    message: z.string(),
  }),
]);
export type WorkflowCreateData = z.infer<typeof WorkflowCreateDataSchema>;

// ---------------------------------------------------------------------------
// workflow.update — with expectedVersion + active-items guard
// ---------------------------------------------------------------------------

export const WorkflowUpdatePayloadSchema = z.object({
  workflowId: z.string(),
  definition: WorkflowDefinitionSchema,
  expectedVersion: z.number(),
});
export type WorkflowUpdatePayload = z.infer<typeof WorkflowUpdatePayloadSchema>;

export const WorkflowUpdateDataSchema = z.union([
  z.object({
    ok: z.literal(true),
    newVersion: z.number(),
    definition: WorkflowDefinitionSchema,
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("version_conflict"),
    currentVersion: z.number(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("validation_failed"),
    errors: z.array(WorkflowValidationErrorSchema),
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("active_items_block"),
    blocks: z.array(z.object({ state: z.string(), count: z.number() })),
  }),
  z.object({
    ok: z.literal(false),
    code: z.literal("not_found"),
  }),
]);
export type WorkflowUpdateData = z.infer<typeof WorkflowUpdateDataSchema>;

// ---------------------------------------------------------------------------
// workflow.setStatus
// ---------------------------------------------------------------------------

export const WorkflowSetStatusPayloadSchema = z.object({
  workflowId: z.string(),
  status: z.enum(["active", "paused", "archived"]),
});
export type WorkflowSetStatusPayload = z.infer<typeof WorkflowSetStatusPayloadSchema>;

export const WorkflowSetStatusDataSchema = z.union([
  z.object({ ok: z.literal(true), status: z.enum(["active", "paused", "archived"]) }),
  z.object({
    ok: z.literal(false),
    code: z.enum(["not_found", "invalid_status_transition"]),
    message: z.string(),
  }),
]);
export type WorkflowSetStatusData = z.infer<typeof WorkflowSetStatusDataSchema>;

// ---------------------------------------------------------------------------
// workflow.items.list — Hub kanban view feeder
// ---------------------------------------------------------------------------

export const WorkflowItemsListPayloadSchema = z.object({
  workflowId: z.string(),
  state: z.string().optional(),
  includeClosed: z.boolean().optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(200).optional(),
});
export type WorkflowItemsListPayload = z.infer<typeof WorkflowItemsListPayloadSchema>;

const WorkflowItemSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  state: z.string(),
  payload: z.record(z.string(), z.unknown()),
  assignee: WorkflowActorRefSchema.nullable(),
  createdBy: WorkflowActorRefSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type WorkflowItemWire = z.infer<typeof WorkflowItemSchema>;

export const WorkflowItemsListDataSchema = z.object({
  items: z.array(WorkflowItemSchema),
  nextCursor: z.string().optional(),
});
export type WorkflowItemsListData = z.infer<typeof WorkflowItemsListDataSchema>;

// ---------------------------------------------------------------------------
// workflow.items.get — Hub item-detail view feeder
// ---------------------------------------------------------------------------

export const WorkflowItemGetPayloadSchema = z.object({
  itemId: z.string(),
  activityLimit: z.number().int().min(1).max(200).optional(),
  commentsLimit: z.number().int().min(1).max(200).optional(),
});
export type WorkflowItemGetPayload = z.infer<typeof WorkflowItemGetPayloadSchema>;

export const WorkflowActivityEntrySchema = z.object({
  id: z.string(),
  itemId: z.string(),
  workflowId: z.string(),
  actor: z.union([
    z.object({ type: z.literal("agent"), id: z.string() }),
    z.object({ type: z.literal("user"), id: z.string() }),
    z.object({ type: z.literal("trigger"), id: z.string() }),
  ]),
  action: z.string(),
  fromState: z.string().optional(),
  toState: z.string().optional(),
  transitionName: z.string().optional(),
  payloadDiff: z.record(z.string(), z.unknown()).optional(),
  correlationId: z.string(),
  occurredAt: z.number(),
});

export const WorkflowCommentSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  workflowId: z.string(),
  author: WorkflowActorRefSchema,
  body: z.string(),
  createdAt: z.number(),
});

export const WorkflowItemGetDataSchema = z.union([
  z.object({
    ok: z.literal(true),
    item: WorkflowItemSchema,
    activityTail: z.array(WorkflowActivityEntrySchema),
    hasMoreActivity: z.boolean(),
    commentsTail: z.array(WorkflowCommentSchema),
    hasMoreComments: z.boolean(),
  }),
  z.object({ ok: z.literal(false), code: z.literal("not_found") }),
]);
export type WorkflowItemGetData = z.infer<typeof WorkflowItemGetDataSchema>;
